import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Button from '../components/Button';
import Icon from '../components/Icon';
import { motion, AnimatePresence } from 'framer-motion';
import engagementService from '../services/engagementService';
import Notification from '../components/Notification';
import useNotification from '../hooks/useNotification';

const ApprovalsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-family: var(--font-heading);
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: var(--text-base);
  margin: 0.35rem 0 0;
  max-width: 62ch;
  line-height: 1.6;
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
  padding: 0.65rem 0.85rem;
  margin-bottom: 1rem;
  background: var(--surface-1);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
`;

const Count = styled.span`
  color: var(--text-secondary);
  font-size: var(--text-sm);
  margin-right: auto;
  padding-left: 0.25rem;
  font-variant-numeric: tabular-nums;
`;



// A default browser checkbox renders light-blue and washed out against the
// dark theme, so it is restyled to match the accent colour.
const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  width: 1.15rem;
  height: 1.15rem;
  margin: 0.2rem 0 0;
  flex-shrink: 0;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: rgba(10, 10, 20, 0.6);
  cursor: pointer;
  position: relative;

  &:checked {
    background: var(--accent-cyan);
    border-color: var(--accent-cyan);
  }

  &:checked::after {
    content: '✓';
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    color: var(--primary-bg);
    font-size: 0.8rem;
    font-weight: 700;
  }

  &:focus-visible { outline: 2px solid var(--accent-cyan); outline-offset: 2px; }
`;

const LoadMore = styled(motion.button)`
  display: block;
  width: 100%;
  padding: 0.9rem;
  margin-top: 0.5rem;
  background: rgba(22, 26, 43, 0.6);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  color: var(--text-primary);
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Card = styled(motion.div)`
  background: rgba(22, 26, 43, 0.6);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  margin-bottom: 1.25rem;
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const Who = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.div`
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1.05rem;
`;

const Meta = styled.div`
  color: var(--text-secondary);
  font-size: 0.85rem;
  margin-top: 0.2rem;
  word-break: break-word;
`;

const Field = styled.div`
  margin-bottom: 0.9rem;
`;

const FieldLabel = styled.label`
  display: block;
  color: var(--text-secondary);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.4rem;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(10, 10, 20, 0.6);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  padding: 0.7rem 0.9rem;
  font-size: 0.95rem;
  font-family: inherit;

  &:focus { outline: none; border-color: var(--accent-cyan); }
`;

const Textarea = styled(Input).attrs({ as: 'textarea' })`
  min-height: 9rem;
  resize: vertical;
  line-height: 1.6;
`;

// "Why this message": the research evidence behind the draft.
const Evidence = styled.details`
  margin: 0.25rem 0 0.9rem;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  background: rgba(10, 10, 20, 0.4);

  summary {
    cursor: pointer;
    padding: 0.55rem 0.9rem;
    color: var(--accent-cyan);
    font-size: 0.8rem;
    font-weight: 600;
    user-select: none;
  }
`;

const EvidenceBody = styled.div`
  padding: 0 0.9rem 0.8rem;
  color: var(--text-secondary);
  font-size: 0.85rem;
  line-height: 1.6;

  a { color: var(--accent-cyan); word-break: break-all; }
`;

const EvidenceLabel = styled.div`
  color: var(--text-primary);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0.6rem 0 0.2rem;
`;

const Snippet = styled.blockquote`
  margin: 0.3rem 0;
  padding: 0.4rem 0.7rem;
  border-left: 2px solid var(--accent-violet, #8A2BE2);
  font-style: italic;
`;

// Playbook-rubric verdict for the draft.
const QualityBadge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  margin-left: 0.5rem;
  background: ${(p) => (p.$verdict === 'pass'
    ? 'rgba(52, 211, 153, 0.15)'
    : p.$verdict === 'review'
      ? 'rgba(251, 191, 36, 0.15)'
      : 'rgba(148, 163, 184, 0.12)')};
  color: ${(p) => (p.$verdict === 'pass' ? '#34D399' : p.$verdict === 'review' ? '#fbbf24' : '#94a3b8')};
  border: 1px solid ${(p) => (p.$verdict === 'pass' ? 'rgba(52, 211, 153,0.4)' : p.$verdict === 'review' ? 'rgba(251, 191, 36,0.4)' : 'rgba(148,163,184,0.3)')};
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Empty = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  background: rgba(22, 26, 43, 0.4);
  border: 1px dashed var(--border-color);
  border-radius: var(--radius-lg);
  line-height: 1.7;
`;

const PAGE_SIZE = 50;

const Approvals = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const { notifications, removeNotification, showSuccess, showError } = useNotification();

  // Seed an editable copy per draft so edits stay local until approval.
  const seedDrafts = (list) => {
    const seed = {};
    list.forEach((i) => {
      seed[i.id] = { messageSubject: i.messageSubject || '', initialMessage: i.initialMessage || '' };
    });
    return seed;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await engagementService.getPending({ limit: PAGE_SIZE, skip: 0 });
      setItems(data.items);
      setTotal(data.total);
      setDrafts(seedDrafts(data.items));
      setSelected([]);
    } catch (error) {
      showError('Could not load the approval queue');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  /** Append the next page rather than replacing, so earlier edits survive. */
  const loadMore = async () => {
    setBusy(true);
    try {
      const data = await engagementService.getPending({ limit: PAGE_SIZE, skip: items.length });
      setItems((prev) => [...prev, ...data.items]);
      setDrafts((prev) => ({ ...prev, ...seedDrafts(data.items) }));
      setTotal(data.total);
    } catch (error) {
      showError('Could not load more');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const editDraft = (id, field, value) => {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], [field]: value } }));
  };

  const toggle = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const act = async (ids, mode) => {
    if (ids.length === 0) return;
    setBusy(true);
    try {
      if (mode === 'approve') {
        // Persist any edits before approving, so what was reviewed is what sends.
        await Promise.all(ids.map((id) => engagementService.updateDraft(id, drafts[id])));
        const { approved } = await engagementService.approve(ids);
        showSuccess(`Approved ${approved} message${approved === 1 ? '' : 's'} for sending`);
      } else {
        const { rejected } = await engagementService.reject(ids);
        showSuccess(`Rejected ${rejected} message${rejected === 1 ? '' : 's'}`);
      }
      await load();
    } catch (error) {
      showError(`Could not ${mode} — please try again`);
    } finally {
      setBusy(false);
    }
  };

  const allSelected = items.length > 0 && selected.length === items.length;

  return (
    <ApprovalsContainer>
      <Notification notifications={notifications} removeNotification={removeNotification} />

      <Header>
        <div>
          <Title>Approvals</Title>
          <Subtitle>
            Drafts wait here until you approve them. Nothing is sent to a prospect
            without your review. Approved messages are paced and sent automatically.
          </Subtitle>
        </div>
      </Header>

      {items.length > 0 && (
        <BulkBar>
          <Count>
            {total} awaiting review · showing {items.length} · {selected.length} selected
          </Count>
          <Button $variant="ghost" type="button" onClick={() => setSelected(allSelected ? [] : items.map((i) => i.id))}>
            {allSelected ? 'Clear selection' : 'Select all'}
          </Button>
          <Button $variant="danger" type="button" disabled={busy || selected.length === 0} onClick={() => act(selected, 'reject')}>
            Reject {selected.length || ''}
          </Button>
          <Button $variant="primary" type="button" disabled={busy || selected.length === 0} onClick={() => act(selected, 'approve')}>
            <Icon name="check" size={14} /> Approve {selected.length || ''}
          </Button>
        </BulkBar>
      )}

      {loading && <Empty>Loading the approval queue…</Empty>}

      {!loading && items.length === 0 && (
        <Empty>
          Nothing is waiting for review.
          <br />
          When a workflow drafts outreach, it will appear here for approval before anything is sent.
        </Empty>
      )}

      <AnimatePresence>
        {!loading && items.map((item) => (
          <Card
            key={item.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <CardHead>
              <Checkbox
                checked={selected.includes(item.id)}
                onChange={() => toggle(item.id)}
                aria-label={`Select message to ${item.prospect?.fullName || 'prospect'}`}
              />
              <Who>
                <Name>
                  {item.prospect?.fullName || 'Unknown prospect'}
                  {item.quality && (
                    <QualityBadge $verdict={item.quality.verdict} title={(item.quality.reasons || []).join('; ') || undefined}>
                      {item.quality.verdict === 'pass' ? `PASS ${item.quality.score}/10`
                        : item.quality.verdict === 'review' ? `REVIEW ${item.quality.score ?? '?'}/10`
                        : 'UNSCORED'}
                    </QualityBadge>
                  )}
                </Name>
                <Meta>
                  {[item.prospect?.title, item.prospect?.organizationName]
                    .filter(Boolean).join(' · ')}
                </Meta>
                <Meta>{item.prospect?.email}</Meta>
              </Who>
            </CardHead>

            {item.evidence && (item.evidence.signal || (item.evidence.sources || []).length > 0 || (item.evidence.docSnippets || []).length > 0) && (
              <Evidence>
                <summary>Why this message</summary>
                <EvidenceBody>
                  {item.evidence.signal && (
                    <>
                      <EvidenceLabel>Signal</EvidenceLabel>
                      <div>{item.evidence.signal}</div>
                    </>
                  )}
                  {(item.evidence.sources || []).length > 0 && (
                    <>
                      <EvidenceLabel>Sources</EvidenceLabel>
                      {item.evidence.sources.map((url) => (
                        <div key={url}><a href={url} target="_blank" rel="noreferrer">{url}</a></div>
                      ))}
                    </>
                  )}
                  {(item.evidence.docSnippets || []).length > 0 && (
                    <>
                      <EvidenceLabel>From your documents</EvidenceLabel>
                      {item.evidence.docSnippets.map((sn, i) => (
                        <Snippet key={i}>{sn.text}{sn.text.length >= 180 ? '…' : ''}</Snippet>
                      ))}
                    </>
                  )}
                  {item.quality && (item.quality.reasons || []).length > 0 && (
                    <>
                      <EvidenceLabel>Quality notes</EvidenceLabel>
                      <div>{item.quality.reasons.join(' · ')}</div>
                    </>
                  )}
                </EvidenceBody>
              </Evidence>
            )}

            <Field>
              <FieldLabel htmlFor={`subject-${item.id}`}>Subject</FieldLabel>
              <Input
                id={`subject-${item.id}`}
                value={drafts[item.id]?.messageSubject ?? ''}
                onChange={(e) => editDraft(item.id, 'messageSubject', e.target.value)}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor={`body-${item.id}`}>Message</FieldLabel>
              <Textarea
                id={`body-${item.id}`}
                value={drafts[item.id]?.initialMessage ?? ''}
                onChange={(e) => editDraft(item.id, 'initialMessage', e.target.value)}
              />
            </Field>

            <Actions>
              <Button $variant="danger" type="button" disabled={busy} onClick={() => act([item.id], 'reject')}>
                Reject
              </Button>
              <Button $variant="primary" type="button" disabled={busy} onClick={() => act([item.id], 'approve')}>
                <Icon name="check" size={14} /> Approve and send
              </Button>
            </Actions>
          </Card>
        ))}
      </AnimatePresence>

      {!loading && items.length < total && (
        <LoadMore type="button" disabled={busy} onClick={loadMore}>
          Load more ({total - items.length} remaining)
        </LoadMore>
      )}
    </ApprovalsContainer>
  );
};

export default Approvals;
